"use client"

import { useTranslations } from "next-intl"
import { useCharacterStore } from "@/features/characters/store"
import { Label } from "@/shared/components/ui/label"
import {
  ImageCardGrid,
  BODY_TYPE_OPTIONS,
  BREAST_SIZE_OPTIONS,
  BUTT_SIZE_OPTIONS,
} from "./appearance-step"
import { cn } from "@/shared/lib/utils"
import { Icon } from "@/shared/components/icons"
import { localizeCreateOptions } from "@/features/create/lib/create-option-labels"

export function BodyStep({ invalidFields = [] }: { invalidFields?: string[] }) {
  const t = useTranslations("create.wizard")
  const tOptions = useTranslations("create.wizard.optionLabels")
  const { draft, updateDraft } = useCharacterStore()
  const currentGender = draft.gender || "female"
  const currentStyle = draft.style || "realistic"
  const isBodyTypeInvalid = invalidFields.includes("bodyType")
  const isBreastSizeInvalid = invalidFields.includes("breastSize")
  const isButtSizeInvalid = invalidFields.includes("buttSize")
  const bodyTypeOptions = localizeCreateOptions(BODY_TYPE_OPTIONS, "bodyType", tOptions)
  const breastSizeOptions = localizeCreateOptions(BREAST_SIZE_OPTIONS, "breastSize", tOptions)
  const buttSizeOptions = localizeCreateOptions(BUTT_SIZE_OPTIONS, "buttSize", tOptions)

  return (
    <div className="space-y-6">
      {/* Body Type */}
      <div
        className={cn(
          "space-y-3 rounded-2xl p-4 transition-all",
          isBodyTypeInvalid && "ring-1 ring-red-500/40 border border-red-500/30 bg-red-500/[0.03]"
        )}
      >
        <div className="flex justify-center">
          <Label className={cn("font-semibold transition-colors", isBodyTypeInvalid ? "text-red-400" : "text-foreground")}>
            {t("fieldLabels.bodyType")}
          </Label>
        </div>
        <ImageCardGrid
          options={bodyTypeOptions}
          selected={draft.bodyType}
          onSelect={(v) => updateDraft({ bodyType: v })}
          gender={currentGender}
          style={currentStyle}
          columns={4}
          compact
        />
        {isBodyTypeInvalid && (
          <div className="flex items-center justify-center gap-1.5">
            <Icon name="attention-circle" size={14} className="size-3.5 text-red-400 shrink-0" />
            <p className="text-xs text-red-400">{t("validation.bodyType")}</p>
          </div>
        )}
      </div>

      {/* Breast Size — always show since gender is always female */}
      {["female", "trans"].includes(draft.gender || "female") && (
        <div
          className={cn(
            "space-y-3 rounded-2xl p-4 transition-all",
            isBreastSizeInvalid && "ring-1 ring-red-500/40 border border-red-500/30 bg-red-500/[0.03]"
          )}
        >
          <div className="flex justify-center">
            <Label className={cn("font-semibold transition-colors", isBreastSizeInvalid ? "text-red-400" : "text-foreground")}>
              {t("fieldLabels.breastSize")}
            </Label>
          </div>
          <ImageCardGrid
            options={breastSizeOptions}
            selected={draft.breastSize}
            onSelect={(v) => updateDraft({ breastSize: v })}
            gender={currentGender}
            style={currentStyle}
            columns={4}
            compact
          />
          {isBreastSizeInvalid && (
            <div className="flex items-center justify-center gap-1.5">
              <Icon name="attention-circle" size={14} className="size-3.5 text-red-400 shrink-0" />
              <p className="text-xs text-red-400">{t("validation.breastSize")}</p>
            </div>
          )}
        </div>
      )}

      {/* Butt Size */}
      <div
        className={cn(
          "space-y-3 rounded-2xl p-4 transition-all",
          isButtSizeInvalid && "ring-1 ring-red-500/40 border border-red-500/30 bg-red-500/[0.03]"
        )}
      >
        <div className="flex justify-center">
          <Label className={cn("font-semibold transition-colors", isButtSizeInvalid ? "text-red-400" : "text-foreground")}>
            {t("fieldLabels.buttSize")}
          </Label>
        </div>
        <ImageCardGrid
          options={buttSizeOptions}
          selected={draft.buttSize}
          onSelect={(v) => updateDraft({ buttSize: v })}
          gender={currentGender}
          style={currentStyle}
          columns={4}
          compact
        />
        {isButtSizeInvalid && (
          <div className="flex items-center justify-center gap-1.5">
            <Icon name="attention-circle" size={14} className="size-3.5 text-red-400 shrink-0" />
            <p className="text-xs text-red-400">{t("validation.buttSize")}</p>
          </div>
        )}
      </div>
    </div>
  )
}
